import { useState, useRef, useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { menuPermissionsApi, authApi, tecniciApi, allegatiTecniciApi } from '../services/api'
import toast from 'react-hot-toast'
import {
  X, Save, UserPlus, Download, RefreshCw, Eye, EyeOff, Table2, LayoutList,
} from 'lucide-react'
import Utenti from './Utenti'
import RichiesteEBlocchi from '../components/utenti/RichiesteEBlocchi'

// ─── Tipi ─────────────────────────────────────────────────────────────────────

interface MenuDef {
  id: string; label: string; category: string; route: string
  icon?: string; min_role: string; order: number; active: boolean; enabled: boolean
}

interface MatrixRow {
  user_id: number; nome: string | null; cognome: string | null; email: string
  role: string; is_active: boolean; telefono_whatsapp: string | null
  tecnico_id: number | null; tecnico_telefono: string | null; veicolo_targa: string | null
  iban: string | null; banca: string | null; tecnico_profilo_incompleto: boolean
  permissions: Record<string, boolean>
}

interface Allegato {
  id: number; tipo: string; corso_ref: string | null; nome_file: string
  mime_type: string; dimensione: number; created_at: string
}

// ─── Costanti ─────────────────────────────────────────────────────────────────

const ROLES = [
  'superadmin','admin','dispatcher','responsabile_tecnico',
  'tecnico_senior','tecnico','contabilita','committente','cliente_finale','readonly',
]

const CAT_META: Record<string, { label: string; color: string; text: string }> = {
  delta_poste:   { label: 'DELTA POSTE',    color: '#1E40AF', text: '#fff' },
  imeco:         { label: 'IMECO',          color: '#7C3AED', text: '#fff' },
  fleet:         { label: 'FLEET',          color: '#0D9488', text: '#fff' },
  configurazione:{ label: 'CONFIGURAZIONE', color: '#374151', text: '#fff' },
  home:          { label: 'HOME',           color: '#DC2626', text: '#fff' },
}
const CAT_DEFAULT = { label: 'ALTRI', color: '#4B5563', text: '#fff' }

const ROLE_COLORS: Record<string, string> = {
  superadmin: '#FEF2F2', admin: '#FFF7ED', dispatcher: '#EFF6FF',
  responsabile_tecnico: '#F0FDF4', tecnico_senior: '#F0FDF4', tecnico: '#F0FDF4',
  contabilita: '#FDF4FF', readonly: '#F9FAFB',
}

const STICKY_WIDTHS = [160, 170, 120, 100, 80] // Nome, Email, Ruolo, Telefono, Targa
const STICKY_LEFTS = STICKY_WIDTHS.reduce<number[]>((acc, w) => {
  acc.push((acc[acc.length - 1] ?? 0) + (acc.length > 0 ? STICKY_WIDTHS[acc.length - 1] : 0))
  return acc
}, [0])

// ─── Componente Slide-over ────────────────────────────────────────────────────

function SlideOver({
  row, menus, onClose, onSaved,
}: {
  row: MatrixRow; menus: MenuDef[]
  onClose: () => void; onSaved: (userId: number, updates: Partial<MatrixRow>) => void
}) {
  const [form, setForm] = useState({
    nome: row.nome ?? '', cognome: row.cognome ?? '',
    email: row.email, role: row.role,
    telefono_whatsapp: row.telefono_whatsapp ?? '',
    tecnico_telefono: row.tecnico_telefono ?? '',
    veicolo_targa: row.veicolo_targa ?? '',
    iban: row.iban ?? '', banca: row.banca ?? '',
    is_active: row.is_active,
  })
  const [saving, setSaving] = useState(false)
  const [showPwReset, setShowPwReset] = useState(false)
  const [newPw, setNewPw] = useState('')
  const [showPw, setShowPw] = useState(false)

  const { data: allegatiRes } = useQuery({
    queryKey: ['allegati-tecnico', row.tecnico_id],
    queryFn: () => allegatiTecniciApi.list(row.tecnico_id!).then(r => r.data as Allegato[]),
    enabled: !!row.tecnico_id,
  })
  const allegati = allegatiRes ?? []

  const handleSave = async () => {
    setSaving(true)
    try {
      const userPayload: Record<string, unknown> = {
        nome: form.nome || null,
        cognome: form.cognome || null,
        email: form.email,
        role: form.role,
        telefono_whatsapp: form.telefono_whatsapp || null,
        is_active: form.is_active,
      }
      if (showPwReset && newPw) userPayload.password = newPw

      await authApi.updateUser(row.user_id, userPayload)

      if (row.tecnico_id) {
        await tecniciApi.update(row.tecnico_id, {
          telefono: form.tecnico_telefono || null,
          veicolo_targa: form.veicolo_targa || null,
          iban: form.iban || null,
          banca: form.banca || null,
        })
      }

      onSaved(row.user_id, {
        nome: form.nome || null, cognome: form.cognome || null, email: form.email,
        role: form.role, is_active: form.is_active,
        telefono_whatsapp: form.telefono_whatsapp || null,
        tecnico_telefono: form.tecnico_telefono || null,
        veicolo_targa: form.veicolo_targa || null,
        iban: form.iban || null, banca: form.banca || null,
      })
      toast.success('Salvato')
      setShowPwReset(false)
      setNewPw('')
    } catch {
      toast.error('Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const downloadAllegato = async (a: Allegato) => {
    try {
      const r = await allegatiTecniciApi.download(row.tecnico_id!, a.id)
      const url = URL.createObjectURL(r.data)
      const link = document.createElement('a')
      link.href = url; link.download = a.nome_file; link.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Download fallito') }
  }

  const field = (label: string, key: keyof typeof form, type = 'text') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </label>
      <input
        type={type}
        value={String(form[key])}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        style={{
          border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 10px',
          fontSize: 13, outline: 'none', background: '#fff',
        }}
        onFocus={e => { e.target.style.borderColor = '#1E40AF' }}
        onBlur={e => { e.target.style.borderColor = '#E5E7EB' }}
      />
    </div>
  )

  return (
    <div
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
        background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        zIndex: 50, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div style={{ padding: '16px 20px', background: '#1E40AF', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{form.nome} {form.cognome}</div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>{form.email}</div>
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer', color: '#fff' }}>
          <X size={16} />
        </button>
      </div>

      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 20, flex: 1 }}>

        {/* Dati Utente */}
        <section>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #E5E7EB' }}>
            Dati Utente
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {field('Nome', 'nome')}
            {field('Cognome', 'cognome')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
            {field('Email', 'email', 'email')}
            {field('Telefono WA', 'telefono_whatsapp')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Ruolo
              </label>
              <select
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 10px', fontSize: 13, background: '#fff' }}
              >
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
              />
              Utente attivo
            </label>
          </div>
        </section>

        {/* Reset Password */}
        <section>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #E5E7EB' }}>
            Password
          </h3>
          {!showPwReset ? (
            <button
              onClick={() => setShowPwReset(true)}
              style={{ fontSize: 12, color: '#1E40AF', background: 'none', border: '1px solid #1E40AF', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}
            >
              Imposta nuova password
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={newPw}
                  onChange={e => setNewPw(e.target.value)}
                  placeholder="Nuova password..."
                  style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 32px 6px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                />
                <button
                  onClick={() => setShowPw(v => !v)}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}
                >
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <button onClick={() => { setShowPwReset(false); setNewPw('') }} style={{ fontSize: 12, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={14} />
              </button>
            </div>
          )}
        </section>

        {/* Dati Tecnico */}
        {row.tecnico_id && (
          <section>
            <h3 style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #E5E7EB' }}>
              Dati Tecnico
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {field('Telefono', 'tecnico_telefono')}
              {field('Targa', 'veicolo_targa')}
              {field('Banca', 'banca')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  IBAN
                </label>
                <input
                  type="text"
                  value={form.iban}
                  placeholder="IT..."
                  onChange={e => setForm(f => ({ ...f, iban: e.target.value.toUpperCase() }))}
                  style={{
                    border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 10px',
                    fontSize: 12, outline: 'none', background: '#fff',
                    fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#1E40AF' }}
                  onBlur={e => { e.target.style.borderColor = '#E5E7EB' }}
                />
              </div>
            </div>
          </section>
        )}

        {/* Allegati */}
        {row.tecnico_id && (
          <section>
            <h3 style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #E5E7EB' }}>
              Documenti ({allegati.length})
            </h3>
            {allegati.length === 0 ? (
              <p style={{ fontSize: 12, color: '#9CA3AF' }}>Nessun documento</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {allegati.map(a => (
                  <div
                    key={a.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 10px', background: '#F9FAFB', borderRadius: 6, border: '1px solid #E5E7EB',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{a.tipo}</div>
                      <div style={{ fontSize: 11, color: '#6B7280' }}>{a.nome_file}</div>
                    </div>
                    <button
                      onClick={() => downloadAllegato(a)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1E40AF', padding: 4 }}
                    >
                      <Download size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Footer Save */}
      <div style={{ padding: '12px 20px', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
        <button
          onClick={onClose}
          style={{ padding: '7px 16px', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
        >
          Annulla
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '7px 16px', fontSize: 13, border: 'none', borderRadius: 6,
            background: '#1E40AF', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.7 : 1,
          }}
        >
          <Save size={13} />
          {saving ? 'Salvataggio...' : 'Salva'}
        </button>
      </div>
    </div>
  )
}

// ─── Modal Nuovo Utente ───────────────────────────────────────────────────────

function CreateUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ nome: '', cognome: '', email: '', password: '', role: 'tecnico' })
  const [saving, setSaving] = useState(false)

  const handleCreate = async () => {
    if (!form.email || !form.password) { toast.error('Email e password obbligatorie'); return }
    setSaving(true)
    try {
      await authApi.createUser(form)
      toast.success('Utente creato')
      onCreated()
      onClose()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      toast.error(err?.response?.data?.detail ?? 'Errore')
    } finally {
      setSaving(false)
    }
  }

  const f = (label: string, key: keyof typeof form, type = 'text') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}</label>
      {key === 'role' ? (
        <select value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          style={{ border: '1px solid #D1D5DB', borderRadius: 6, padding: '7px 10px', fontSize: 13 }}>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      ) : (
        <input type={type} value={form[key]}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          style={{ border: '1px solid #D1D5DB', borderRadius: 6, padding: '7px 10px', fontSize: 13 }} />
      )}
    </div>
  )

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 400, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Nuovo Utente</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={18} /></button>
        </div>
        {f('Nome', 'nome')}
        {f('Cognome', 'cognome')}
        {f('Email', 'email', 'email')}
        {f('Password', 'password', 'password')}
        {f('Ruolo', 'role')}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button onClick={onClose} style={{ padding: '7px 16px', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer' }}>Annulla</button>
          <button onClick={handleCreate} disabled={saving}
            style={{ padding: '7px 16px', fontSize: 13, background: '#1E40AF', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Creazione...' : 'Crea Utente'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Pagina principale ────────────────────────────────────────────────────────

export default function GestioneUtentiExcel() {
  const { user: me } = useAuth()
  const qc = useQueryClient()

  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<'excel' | 'classico' | 'sicurezza'>(
    searchParams.get('richiesta') || searchParams.get('bloccati') || searchParams.get('reset') || searchParams.get('totp_reset') ? 'sicurezza' : 'excel'
  )
  const [rows, setRows] = useState<MatrixRow[]>([])
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<MatrixRow | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [dirty, setDirty] = useState<Record<number, boolean>>({})
  const [saving, setSaving] = useState<Record<number, boolean>>({})

  const pendingPerms = useRef<Record<number, Record<string, boolean>>>({})
  const permTimers  = useRef<Record<number, ReturnType<typeof setTimeout>>>({})
  const fieldTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})
  const pendingFields = useRef<Record<number, Partial<MatrixRow>>>({})

  const { data: menusRes, isLoading: loadingMenus } = useQuery({
    queryKey: ['all-menus'],
    queryFn: () => menuPermissionsApi.allMenus().then(r => r.data as MenuDef[]),
  })
  const menus = menusRes ?? []

  const { data: matrixRes, isLoading: loadingMatrix, refetch } = useQuery({
    queryKey: ['users-matrix'],
    queryFn: () => menuPermissionsApi.matrix().then(r => r.data as MatrixRow[]),
  })

  useEffect(() => {
    if (matrixRes) setRows(matrixRes)
  }, [matrixRes])

  // Raggruppa menu per categoria
  const categories = menus.reduce<Record<string, MenuDef[]>>((acc, m) => {
    if (!acc[m.category]) acc[m.category] = []
    acc[m.category].push(m)
    return acc
  }, {})

  const catOrder = ['delta_poste', 'imeco', 'fleet', 'configurazione', 'home', ...Object.keys(categories).filter(k => !['delta_poste','imeco','fleet','configurazione','home'].includes(k))]
  const orderedCats = catOrder.filter(c => categories[c]?.length)

  const filteredRows = filter.trim()
    ? rows.filter(r =>
        `${r.nome} ${r.cognome} ${r.email} ${r.role}`.toLowerCase().includes(filter.toLowerCase())
      )
    : rows

  // ── Permission toggle (auto-save debounced) ───────────────────────────────

  const savePerms = useCallback(async (userId: number) => {
    const perms = pendingPerms.current[userId]
    if (!perms) return
    setSaving(p => ({ ...p, [userId]: true }))
    try {
      await menuPermissionsApi.updateUserMenus(
        userId,
        Object.entries(perms).map(([menu_id, enabled]) => ({ menu_id, enabled }))
      )
      setDirty(p => { const n = { ...p }; delete n[userId]; return n })
    } catch {
      toast.error('Errore salvataggio permessi')
    } finally {
      setSaving(p => ({ ...p, [userId]: false }))
    }
  }, [])

  const handlePermToggle = useCallback((userId: number, menuId: string, checked: boolean) => {
    setRows(prev => {
      const next = prev.map(r =>
        r.user_id === userId
          ? { ...r, permissions: { ...r.permissions, [menuId]: checked } }
          : r
      )
      const row = next.find(r => r.user_id === userId)
      if (row) pendingPerms.current[userId] = row.permissions
      return next
    })
    setDirty(p => ({ ...p, [userId]: true }))
    clearTimeout(permTimers.current[userId])
    permTimers.current[userId] = setTimeout(() => savePerms(userId), 600)
  }, [savePerms])

  // ── Field change (auto-save debounced) ────────────────────────────────────

  const saveFields = useCallback(async (userId: number) => {
    const row = rows.find(r => r.user_id === userId)
    const pending = pendingFields.current[userId]
    if (!row || !pending) return
    setSaving(p => ({ ...p, [userId]: true }))
    try {
      await authApi.updateUser(userId, {
        role: pending.role ?? row.role,
        telefono_whatsapp: pending.telefono_whatsapp ?? row.telefono_whatsapp,
        is_active: row.is_active,
      })
      if (row.tecnico_id && (pending.veicolo_targa !== undefined || pending.tecnico_telefono !== undefined)) {
        await tecniciApi.update(row.tecnico_id, {
          veicolo_targa: pending.veicolo_targa ?? row.veicolo_targa,
          telefono: pending.tecnico_telefono ?? row.tecnico_telefono,
        })
      }
      setDirty(p => { const n = { ...p }; delete n[userId]; return n })
      delete pendingFields.current[userId]
    } catch {
      toast.error('Errore salvataggio dati')
    } finally {
      setSaving(p => ({ ...p, [userId]: false }))
    }
  }, [rows])

  const handleFieldChange = useCallback((userId: number, field: keyof MatrixRow, value: unknown) => {
    setRows(prev => prev.map(r => r.user_id === userId ? { ...r, [field]: value } : r))
    pendingFields.current[userId] = { ...(pendingFields.current[userId] ?? {}), [field]: value }
    setDirty(p => ({ ...p, [userId]: true }))
    clearTimeout(fieldTimers.current[userId])
    fieldTimers.current[userId] = setTimeout(() => saveFields(userId), 800)
  }, [saveFields])

  const handleSlideoverSaved = (userId: number, updates: Partial<MatrixRow>) => {
    setRows(prev => prev.map(r => r.user_id === userId ? { ...r, ...updates } : r))
    if (selected?.user_id === userId) setSelected(prev => prev ? { ...prev, ...updates } : null)
  }

  const isAdmin = ['superadmin', 'admin'].includes(me?.role ?? '')

  // ── Sticky helpers ────────────────────────────────────────────────────────

  const stickyTh = (i: number, extra?: React.CSSProperties): React.CSSProperties => ({
    position: 'sticky',
    left: STICKY_LEFTS[i],
    zIndex: 3,
    background: '#1E40AF',
    width: STICKY_WIDTHS[i],
    minWidth: STICKY_WIDTHS[i],
    ...extra,
  })

  const stickyTd = (i: number, bg: string): React.CSSProperties => ({
    position: 'sticky',
    left: STICKY_LEFTS[i],
    zIndex: 2,
    background: bg,
    width: STICKY_WIDTHS[i],
    minWidth: STICKY_WIDTHS[i],
    borderRight: i === STICKY_WIDTHS.length - 1 ? '2px solid #E5E7EB' : undefined,
  })

  if (loadingMenus || loadingMatrix) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#6B7280' }}>
        <RefreshCw size={20} style={{ marginRight: 8 }} className="animate-spin" />
        Caricamento...
      </div>
    )
  }

  const tabBtn = (id: 'excel' | 'classico' | 'sicurezza', icon: React.ReactNode, label: string) => (
    <button
      onClick={() => setTab(id)}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 14px', fontSize: 13, fontWeight: 600,
        border: 'none', borderRadius: 7, cursor: 'pointer',
        background: tab === id ? '#1E40AF' : 'transparent',
        color: tab === id ? '#fff' : '#6B7280',
        transition: 'all 0.15s',
      }}
    >
      {icon}
      {label}
    </button>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

      {/* Header */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: '#fff' }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: '#111827', letterSpacing: '-0.02em' }}>
            Utenti & Permessi
          </h1>
          <p style={{ fontSize: 12, color: '#6B7280' }}>{rows.length} utenti — {menus.length} voci menu</p>
        </div>
        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 4, background: '#F3F4F6', borderRadius: 9, padding: 3 }}>
          {tabBtn('excel',    <Table2 size={13} />,    'Excel')}
          {tabBtn('classico', <LayoutList size={13} />, 'Classico')}
          {me?.role === 'superadmin' && tabBtn('sicurezza', <UserPlus size={13} />, 'Richieste & Blocchi')}
        </div>
        <input
          placeholder="Cerca nome, email, ruolo..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{
            border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 12px', fontSize: 13,
            width: 220, outline: 'none',
          }}
        />
        <button
          onClick={() => refetch()}
          style={{ padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', cursor: 'pointer', color: '#6B7280' }}
          title="Ricarica"
        >
          <RefreshCw size={14} />
        </button>
        {isAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            style={{
              padding: '7px 14px', border: 'none', borderRadius: 8, background: '#1E40AF',
              color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <UserPlus size={14} />
            Aggiungi
          </button>
        )}
      </div>

      {/* Vista Classica */}
      {tab === 'classico' && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          <Utenti />
        </div>
      )}

      {tab === 'sicurezza' && me?.role === 'superadmin' && <RichiesteEBlocchi />}

      {tab === 'excel' && <>
      {/* Legend */}
      <div style={{ padding: '6px 20px', display: 'flex', gap: 10, alignItems: 'center', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB', flexShrink: 0, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>Categorie permessi:</span>
        {orderedCats.map(c => {
          const meta = CAT_META[c] ?? CAT_DEFAULT
          return (
            <span key={c} style={{ fontSize: 10, fontWeight: 700, background: meta.color, color: meta.text, padding: '2px 8px', borderRadius: 99 }}>
              {meta.label}
            </span>
          )
        })}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9CA3AF' }}>
          🟠 = modifiche non salvate &nbsp;·&nbsp; Auto-save attivo
        </span>
      </div>

      {/* Tabella */}
      <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', minHeight: 0 }}>
        <table style={{ borderCollapse: 'collapse', minWidth: 'max-content', fontSize: 12 }}>
          <thead>
            {/* Riga categorie */}
            <tr>
              {/* Sticky cols header span */}
              <th
                colSpan={5}
                style={{
                  position: 'sticky', left: 0, zIndex: 4,
                  background: '#1E40AF', color: '#fff', fontWeight: 700,
                  fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
                  padding: '6px 10px', textAlign: 'left',
                  width: STICKY_WIDTHS.reduce((a, b) => a + b, 0),
                  minWidth: STICKY_WIDTHS.reduce((a, b) => a + b, 0),
                  borderRight: '2px solid rgba(255,255,255,0.3)',
                }}
              >
                DATI UTENTE
              </th>
              {orderedCats.map(c => {
                const meta = CAT_META[c] ?? CAT_DEFAULT
                const count = categories[c].length
                return (
                  <th
                    key={c}
                    colSpan={count}
                    style={{
                      background: meta.color, color: meta.text, fontSize: 9,
                      fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                      padding: '6px 4px', textAlign: 'center',
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                    }}
                  >
                    {meta.label}
                  </th>
                )
              })}
            </tr>

            {/* Riga etichette colonne */}
            <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
              <th style={{ ...stickyTh(0), color: '#fff', padding: '4px 10px', textAlign: 'left', fontWeight: 700, fontSize: 11 }}>
                Nome
              </th>
              <th style={{ ...stickyTh(1), color: '#fff', padding: '4px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11 }}>
                Email
              </th>
              <th style={{ ...stickyTh(2), color: '#fff', padding: '4px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11 }}>
                Ruolo
              </th>
              <th style={{ ...stickyTh(3), color: '#fff', padding: '4px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11 }}>
                Tel. WA
              </th>
              <th style={{ ...stickyTh(4, { borderRight: '2px solid rgba(255,255,255,0.4)' }), color: '#fff', padding: '4px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11 }}>
                Targa
              </th>
              {orderedCats.flatMap(c =>
                categories[c].map(m => (
                  <th
                    key={m.id}
                    title={m.label}
                    style={{
                      width: 30, minWidth: 30, maxWidth: 30,
                      padding: '0 2px', verticalAlign: 'bottom',
                      background: '#F9FAFB',
                      borderRight: '1px solid #E5E7EB',
                    }}
                  >
                    <div style={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      fontSize: 10, fontWeight: 600, color: '#374151',
                      padding: '6px 0', height: 80, display: 'flex',
                      alignItems: 'center', whiteSpace: 'nowrap',
                    }}>
                      {m.label}
                    </div>
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, ri) => {
              const rowBg = ROLE_COLORS[row.role] ?? '#fff'
              const isDirty = dirty[row.user_id]
              const isSaving = saving[row.user_id]

              return (
                <tr
                  key={row.user_id}
                  style={{
                    borderBottom: '1px solid #E5E7EB',
                    background: ri % 2 === 0 ? rowBg : '#fff',
                    borderLeft: isDirty ? '3px solid #F97316' : isSaving ? '3px solid #22C55E' : '3px solid transparent',
                  }}
                >
                  {/* Nome */}
                  <td style={{ ...stickyTd(0, ri % 2 === 0 ? rowBg : '#fff'), padding: '4px 10px' }}>
                    <button
                      onClick={() => setSelected(row)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#1E40AF', fontWeight: 700, fontSize: 12,
                        textAlign: 'left', textDecoration: 'underline',
                        padding: 0, whiteSpace: 'nowrap',
                      }}
                    >
                      {row.nome ?? ''} {row.cognome ?? ''}
                      {!row.is_active && <span style={{ fontSize: 9, color: '#EF4444', marginLeft: 4 }}>[disattivo]</span>}
                    </button>
                    {row.tecnico_profilo_incompleto && (
                      <span title="Profilo tecnico incompleto — mancano dati come codice fiscale, categoria contrattuale, ecc."
                        style={{ display: 'inline-block', fontSize: 9, fontWeight: 700, color: '#C2410C', background: '#FFF7ED',
                          border: '1px solid #FED7AA', borderRadius: 4, padding: '1px 5px', marginLeft: 6, verticalAlign: 'middle' }}>
                        incompleto
                      </span>
                    )}
                  </td>

                  {/* Email */}
                  <td style={{ ...stickyTd(1, ri % 2 === 0 ? rowBg : '#fff'), padding: '4px 8px', color: '#6B7280', fontSize: 11 }}>
                    <span style={{ display: 'block', maxWidth: 165, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.email}
                    </span>
                  </td>

                  {/* Ruolo */}
                  <td style={{ ...stickyTd(2, ri % 2 === 0 ? rowBg : '#fff'), padding: '2px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <select
                        value={row.role}
                        onChange={e => handleFieldChange(row.user_id, 'role', e.target.value)}
                        disabled={!isAdmin}
                        style={{
                          border: '1px solid transparent', borderRadius: 4, fontSize: 11,
                          background: 'transparent', cursor: isAdmin ? 'pointer' : 'default',
                          padding: '2px 2px', width: '100%', fontWeight: 600,
                          color: '#374151',
                        }}
                        onFocus={e => { e.target.style.border = '1px solid #1E40AF'; e.target.style.background = '#fff' }}
                        onBlur={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent' }}
                      >
                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </td>

                  {/* Telefono WA */}
                  <td style={{ ...stickyTd(3, ri % 2 === 0 ? rowBg : '#fff'), padding: '2px 8px' }}>
                    <input
                      type="text"
                      value={row.telefono_whatsapp ?? ''}
                      onChange={e => handleFieldChange(row.user_id, 'telefono_whatsapp', e.target.value)}
                      disabled={!isAdmin}
                      placeholder="—"
                      style={{
                        border: '1px solid transparent', borderRadius: 4, fontSize: 11,
                        background: 'transparent', width: '100%', padding: '2px 4px',
                      }}
                      onFocus={e => { e.target.style.border = '1px solid #1E40AF'; e.target.style.background = '#fff' }}
                      onBlur={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent' }}
                    />
                  </td>

                  {/* Targa */}
                  <td style={{ ...stickyTd(4, ri % 2 === 0 ? rowBg : '#fff'), padding: '2px 8px', borderRight: '2px solid #E5E7EB' }}>
                    {row.tecnico_id ? (
                      <input
                        type="text"
                        value={row.veicolo_targa ?? ''}
                        onChange={e => handleFieldChange(row.user_id, 'veicolo_targa', e.target.value)}
                        disabled={!isAdmin}
                        placeholder="—"
                        style={{
                          border: '1px solid transparent', borderRadius: 4, fontSize: 11,
                          background: 'transparent', width: '100%', padding: '2px 4px',
                          textTransform: 'uppercase',
                        }}
                        onFocus={e => { e.target.style.border = '1px solid #1E40AF'; e.target.style.background = '#fff' }}
                        onBlur={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent' }}
                      />
                    ) : (
                      <span style={{ color: '#D1D5DB', fontSize: 11 }}>—</span>
                    )}
                  </td>

                  {/* Permission checkboxes */}
                  {orderedCats.flatMap(c =>
                    categories[c].map(m => {
                      const checked = row.permissions[m.id] ?? false
                      return (
                        <td
                          key={m.id}
                          style={{ textAlign: 'center', padding: '2px 0', borderRight: '1px solid #F3F4F6' }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => handlePermToggle(row.user_id, m.id, e.target.checked)}
                            disabled={!isAdmin}
                            style={{ cursor: isAdmin ? 'pointer' : 'default', accentColor: (CAT_META[c] ?? CAT_DEFAULT).color }}
                          />
                        </td>
                      )
                    })
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Slide-over */}
      {selected && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 49 }}
            onClick={() => setSelected(null)}
          />
          <SlideOver
            row={selected}
            menus={menus}
            onClose={() => setSelected(null)}
            onSaved={handleSlideoverSaved}
          />
        </>
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { refetch(); qc.invalidateQueries({ queryKey: ['users-matrix'] }) }}
        />
      )}
      </>}
    </div>
  )
}
